import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { useSelector } from "react-redux";
import {
  downloadRoleDataToCsv,
  downloadRoleDataToXlsx,
  getRoleList,
} from "../../app/features/role/roleSlice";
import getAxiosError from "../../app/api/axiosError";
import { toast } from "react-toastify";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { RoleResponse } from "../../app/models/responseModels/roleResponse";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import { ROLE_COLUMN_NAME, ROLE_LABEL_NAME } from "./RoleConstants";
import CommandOpsDataGrid from "../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import CachedIcon from "@mui/icons-material/Cached";
import DownloadIcon from "@mui/icons-material/Download";
import { PAGES } from "../../enums/PagesEnum";
import { downloadCsv, downloadXlsx } from "../../helpers/downloadFile";
import EditRoleModal from "./modals/EditRoleModal";
import {
  useSubmitModal,
  useSubmitModalWithParameter,
} from "../../components/common/modalPopup/useSubmitModal";
import AddRoleModal from "./modals/AddRoleModal";
import RoleDataFilter from "./RoleDataFilter";
import { formatISODateString } from "../../components/common/utils/DateUtils";

const breadcrumb = ["Role"];

const Role = () => {
  const dispatch = useAppDispatch();
  const { isLoading, roleList } = useSelector((state: RootState) => state.role);

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.ROLE
    )
  );

  const isFdp = useSelector(
    (state: RootState) => state.login?.authResponse?.isFdp
  );

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );

  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;

  // ***** States *****
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [filteredData, setFilteredData] = useState<RoleResponse[]>([]);

  // ***** Callbacks *****
  const getRoleData = useCallback(async () => {
    try {
      const response = await dispatch(getRoleList(isFdp as boolean)).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "roleList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "roleListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getRoleData();
    }

    return () => {
      isFunctionRan = false;
    };
  }, [getRoleData]);

  useEffect(() => {
    if (roleList.length > 0) {
      if (filteredData.length === roleList.length) {
        setFilteredData(roleList);
      }
    } else {
      setFilteredData([]);
    }
  }, [roleList, filteredData]);

  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<RoleResponse>[]>(
    () => [
      {
        accessorKey: ROLE_COLUMN_NAME.NAME,
        header: ROLE_LABEL_NAME.NAME,
        size: 180,
        filterVariant: "autocomplete",
      },
      {
        accessorKey: ROLE_COLUMN_NAME.CREATED_ON,
        header: ROLE_LABEL_NAME.CREATED_ON,
        size: 80,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
      {
        accessorKey: ROLE_COLUMN_NAME.UPDATED_ON,
        header: ROLE_LABEL_NAME.UPDATED_ON,
        size: 200,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
      {
        accessorKey: ROLE_COLUMN_NAME.DELETED,
        header: ROLE_LABEL_NAME.DELETED,
        size: 300,
        Cell: ({ cell, row }) => (
          <div>{<span>{row.original.deleted ? "True" : "False"}</span>}</div>
        ),
      },
    ],
    []
  );

  // ***** Handlers *****
  const handleRefresh = () => {
    getRoleData();
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadRoleDataToCsv(filteredData)
      ).unwrap();

      if (response.isSuccess) {
        downloadCsv(response.data);
      } else {
        toast.error(response.message!, {
          toastId: "roleDownloadCsv",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "roleDownloadCsvException",
      });
    }
  };

  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadRoleDataToXlsx(filteredData)
      ).unwrap();

      if (response) {
        downloadXlsx(response, "Role.xlsx");
      } else {
        toast.error("unable to download file", {
          toastId: "roleDownloadXlsx",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "roleDownloadXlsxException",
      });
    }
  };

  // ***** Modal Popups *****
  const [editRoleModal, openEditRoleModal] =
    useSubmitModalWithParameter<RoleResponse>(
      (roleRowData, closeEditRoleModal) => (
        <EditRoleModal
          closeEditRoleModal={closeEditRoleModal}
          onCompleted={getRoleData}
          roleRowData={roleRowData}
        />
      )
    );

  const [addRoleModal, openAddRoleModal] = useSubmitModal(
    (closeAddRoleModal) => (
      <AddRoleModal
        closeAddRoleModal={closeAddRoleModal}
        onCompleted={getRoleData}
      />
    )
  );

  // ***** Datagrid Custom Actions *****
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<RoleResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={handleRefresh}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Download/Export">
          <IconButton type="button" onClick={handleDownloadMenuClick}>
            <DownloadIcon sx={{ color: "blue" }} />
          </IconButton>
        </CustomTooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={(event) => handleCsvFileDownload(event)}>
            Csv
          </MenuItem>
          <MenuItem onClick={(event) => handleXlsxFileDownload(event)}>
            Xlsx
          </MenuItem>
        </Menu>
        {IsCreatePermission && IsAdmin && (
          <Button
            color="success"
            disabled={isLoading}
            onClick={() => openAddRoleModal()}
            variant="contained"
            size="small"
            sx={{ ml: 1 }}
          >
            Add
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      <>
        <RoleDataFilter
          roleData={roleList}
          setFilteredData={setFilteredData}
          filteredData={filteredData}
        />
        <Divider />
        <br />
      </>
      <CommandOpsDataGrid
        data={filteredData}
        columns={columns}
        isLoading={isLoading}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        emptyRowMessage={"No roles"}
        rowCount={filteredData.length.toLocaleString()}
        isDescending={false}
        defaultSortColumnName="name"
        openModal={openEditRoleModal}
      />
      {editRoleModal}
      {addRoleModal}
    </Box>
  );
};

export default Role;
